import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { Heart, Menu, ShoppingCart } from 'lucide-react';
import { APP_NAME, ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { useCartItemCount } from '../../cart/hooks/use-cart.js';
import { CartDrawer } from '../../cart/components/cart-drawer.js';
import { useWishlistItemCount } from '../../wishlist/hooks/use-wishlist.js';
import { UserMenu } from '../../../../components/user-menu.js';
import { SearchBar } from './search-bar.js';
import { CategoryNav } from './category-nav.js';
import { MobileMenu } from './mobile-menu.js';
import { HeaderIconLink } from './header-icon-link.js';
import { EarnWithFunButton } from '../../rewards/components/earn-with-fun-button.js';

const PRIMARY_NAV = [
  { to: ROUTE_NAMES.home, label: 'Home', end: true },
  { to: ROUTE_NAMES.shop, label: 'Shop', end: false },
  { to: ROUTE_NAMES.about, label: 'About', end: false },
  { to: ROUTE_NAMES.contact, label: 'Contact', end: false },
] as const;

/**
 * Sticky header: hamburger (mobile) + logo + desktop nav (active route in a
 * yellow pill) + search + Earn-with-Fun + cart/wishlist badges + account
 * menu/sign-in. The cart badge is shown to every visitor, signed in or not
 * -- guest carts exist now (see docs/cart-architecture.md "Guest carts"),
 * so cart is no longer an authenticated-only feature the way wishlist
 * still is (wishlist stays `<SignedIn>`-gated -- no guest-wishlist concept
 * exists). Clicking the cart icon opens the mini-cart drawer instead of
 * navigating away, for both identities. Counts come from the same shared
 * queries the cart/wishlist pages themselves use, so a badge update after a
 * mutation elsewhere in the app is automatic.
 */
export function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const { isLoaded } = useAuth();
  const cartCount = useCartItemCount();
  const wishlistCount = useWishlistItemCount();

  return (
    <div className="sticky top-0 z-30 border-b border-gray-100 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
      <header className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          className="focus-visible:ring-brand flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 lg:hidden"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>

        <Link to={ROUTE_NAMES.home} className="flex shrink-0 items-center gap-2.5">
          <span
            aria-hidden="true"
            className="bg-brand text-brand-foreground ring-1 ring-black/5 flex h-9 w-9 items-center justify-center rounded-xl text-base font-black leading-none shadow-sm"
          >
            T
          </span>
          <span className="hidden text-lg font-extrabold leading-none tracking-tight text-slate-900 sm:inline">
            {APP_NAME}
          </span>
        </Link>

        <nav aria-label="Primary" className="ml-2 hidden items-center gap-1 text-sm font-semibold text-slate-600 lg:flex">
          {PRIMARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'rounded-full px-4 py-2 transition-colors',
                  isActive ? 'bg-brand text-brand-foreground' : 'hover:bg-gray-100 hover:text-slate-900',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden flex-1 justify-center px-4 md:flex">
          <SearchBar className="max-w-md" />
        </div>

        <div className="ml-auto flex items-center gap-1.5 sm:gap-2.5">
          <EarnWithFunButton className="hidden sm:inline-flex" />
          <SignedIn>
            <HeaderIconLink to={ROUTE_NAMES.accountWishlist} label="Wishlist" icon={Heart} count={wishlistCount} />
          </SignedIn>
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            aria-label={cartCount > 0 ? `Cart (${cartCount} items)` : 'Cart'}
            className="focus-visible:ring-brand relative flex h-9 w-9 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2"
          >
            <ShoppingCart className="h-5 w-5" aria-hidden="true" />
            {cartCount > 0 && (
              <span className="bg-brand text-brand-foreground absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold">
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            )}
          </button>
          {isLoaded && (
            <>
              <SignedIn>
                <UserMenu />
              </SignedIn>
              <SignedOut>
                <div className="flex items-center gap-2">
                  <Link
                    to={ROUTE_NAMES.signIn}
                    className="rounded-full px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-gray-100"
                  >
                    Log in
                  </Link>
                  <Link
                    to={ROUTE_NAMES.signUp}
                    className="bg-brand text-brand-foreground hover:bg-brand/90 rounded-full px-4 py-2 text-sm font-semibold shadow-sm"
                  >
                    Sign up
                  </Link>
                </div>
              </SignedOut>
            </>
          )}
        </div>
      </header>

      <div className="px-4 pb-3 md:hidden">
        <SearchBar />
      </div>

      <CategoryNav />

      <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} />
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}
